import { createColorTransform, linearToRgb, saturation } from '@limonade/color-science';
import { CorrectionEstimator } from './CorrectionEstimator.js';
export const TONAL_RANGES = ['shadows', 'midtones', 'highlights'];
const clamp = (value, low = 0, high = 1) => Math.max(low, Math.min(high, value));
const round = (value) => Number(value.toFixed(5));
const smoothstep = (low, high, value) => {
    const t = clamp((value - low) / (high - low));
    return t * t * (3 - 2 * t);
};
/** Smooth partition of unity: no abrupt LUT discontinuities at tonal boundaries. */
export function tonalRangeWeights(level) {
    const shadows = 1 - smoothstep(0.2, 0.35, level);
    const highlights = smoothstep(0.65, 0.8, level);
    return [shadows, 1 - shadows - highlights, highlights];
}
/** Compile once, reuse the existing CMF transforms; ACEScg is not treated as encoded sRGB. */
export function createTonalLuminanceReader(workingColorSpace = 'sRGB') {
    const linear = createColorTransform({ from: workingColorSpace, to: 'sRGB', outputTransferFunction: 'Linear' });
    return (pixel) => {
        const rgb = linear.rgb(pixel);
        return clamp(linearToRgb(Math.max(0, rgb.r * 0.2126 + rgb.g * 0.7152 + rgb.b * 0.0722)));
    };
}
/** Deterministic, scene-independent tone measurements. No extra trained model. */
export class TonalDistributionAnalyzer {
    analyze(reference, source, options = {}) {
        const workingColorSpace = options.workingColorSpace ?? 'sRGB';
        const maxSamples = options.maxSamples ?? 6_000;
        if (!Number.isInteger(maxSamples) || maxSamples < 1)
            throw new Error('Tonal maxSamples must be a positive integer.');
        const level = createTonalLuminanceReader(workingColorSpace);
        const display = createColorTransform({ from: workingColorSpace, to: 'sRGB' });
        const warnings = [...display.warnings];
        const collect = (input) => {
            const accumulators = TONAL_RANGES.map(() => ({ samples: [], total: 0, effective: 0, luma: 0, sat: 0, rgb: [0, 0, 0], bias: [0, 0, 0] }));
            let count = 0;
            let totalWeight = 0;
            const stride = Math.max(1, Math.ceil(input.length / maxSamples));
            for (let index = 0; index < input.length; index += stride) {
                const pixel = input[index];
                const pixelWeight = pixel.weight ?? 1;
                if (![pixel.r, pixel.g, pixel.b, pixelWeight].every(Number.isFinite) || pixelWeight <= 0)
                    continue;
                count++;
                totalWeight += pixelWeight;
                const luma = level(pixel);
                const weights = tonalRangeWeights(luma);
                const shown = display.rgb(pixel);
                const neutral = (shown.r + shown.g + shown.b) / 3;
                for (let band = 0; band < 3; band++) {
                    const region = accumulators[band];
                    const tonalWeight = weights[band];
                    if (tonalWeight <= 0)
                        continue;
                    const weight = tonalWeight * pixelWeight;
                    region.total += weight;
                    region.effective += tonalWeight;
                    region.luma += luma * weight;
                    region.sat += clamp(saturation(pixel)) * weight;
                    region.rgb[0] += pixel.r * weight;
                    region.rgb[1] += pixel.g * weight;
                    region.rgb[2] += pixel.b * weight;
                    region.bias[0] += (shown.r - neutral) * weight;
                    region.bias[1] += (shown.g - neutral) * weight;
                    region.bias[2] += (shown.b - neutral) * weight;
                    // Near-black noise / clipped highlights cannot supply reliable hue evidence.
                    if (luma >= 0.035 && luma <= 0.98)
                        region.samples.push({ ...pixel, weight });
                }
            }
            return {
                count,
                regions: accumulators.map((region) => ({
                    samples: region.samples,
                    statistics: {
                        sampleCount: region.samples.length,
                        effectiveSampleCount: round(region.effective),
                        coveragePercentage: round(totalWeight ? region.total / totalWeight * 100 : 0),
                        meanLuminance: round(region.total ? region.luma / region.total : 0),
                        meanSaturation: round(region.total ? region.sat / region.total : 0),
                        meanRGB: region.rgb.map((value) => round(region.total ? value / region.total : 0)),
                        colorBias: region.bias.map((value) => round(region.total ? value / region.total : 0)),
                    },
                })),
            };
        };
        const ref = collect(reference);
        const src = collect(source);
        const estimator = new CorrectionEstimator();
        const bands = TONAL_RANGES.map((range, index) => {
            const r = ref.regions[index], s = src.regions[index];
            const support = Math.min(r.statistics.effectiveSampleCount, s.statistics.effectiveSampleCount);
            const populationGap = Math.abs(r.statistics.coveragePercentage - s.statistics.coveragePercentage) / 100;
            const confidence = support < 8 ? 0 : round(clamp(support / 64) * (1 - populationGap * 0.65));
            const colorVectors = estimator.estimateSixVectorColorMatch(r.samples, s.samples, true);
            const luminanceMatchScore = confidence ? round(Math.exp(-Math.abs(r.statistics.meanLuminance - s.statistics.meanLuminance) * 7)) : null;
            const saturationMatchScore = confidence ? round(Math.exp(-Math.abs(r.statistics.meanSaturation - s.statistics.meanSaturation) * 3)) : null;
            const reliableVectors = colorVectors.vectors.filter((vector) => vector.confidence >= 0.25 && vector.referenceHue !== null && vector.sourceHue !== null);
            const vectorWeight = reliableVectors.reduce((sum, vector) => sum + vector.confidence, 0);
            const hueScore = vectorWeight ? reliableVectors.reduce((sum, vector) => {
                const distance = Math.abs(((vector.referenceHue - vector.sourceHue + 540) % 360) - 180);
                return sum + Math.exp(-distance / 30) * vector.confidence;
            }, 0) / vectorWeight : undefined;
            const biasDistance = Math.hypot(...r.statistics.colorBias.map((value, channel) => value - s.statistics.colorBias[channel]));
            const colorScore = Math.exp(-biasDistance * 4);
            const matchScore = confidence ? round(luminanceMatchScore * (hueScore === undefined ? 0.5 : 0.4) + saturationMatchScore * 0.2 +
                colorScore * (hueScore === undefined ? 0.3 : 0.2) + (hueScore ?? 0) * 0.2) : null;
            if (support < 8)
                warnings.push(`Insufficient ${range} samples in both images; tonal correction for this range is disabled.`);
            if (populationGap > 0.35)
                warnings.push(`${range} coverage differs substantially; scene composition may affect the tonal comparison.`);
            return { range, reference: r.statistics, source: s.statistics, confidence, matchScore, luminanceMatchScore, saturationMatchScore, colorVectors };
        });
        const reliable = bands.filter((band) => band.confidence >= 0.25 && band.matchScore !== null);
        const totalConfidence = reliable.reduce((sum, band) => sum + band.confidence, 0);
        // Do not reward losing an unmatched range by omitting it from the average.
        const coverageOverlap = bands.reduce((sum, band) => sum + Math.min(band.reference.coveragePercentage, band.source.coveragePercentage), 0) / 100;
        return {
            schema: 'limonade-pce-tonal/v1', workingColorSpace, luminanceDomain: 'sRGB-encoded-linear-Y',
            shadowTransition: [0.2, 0.35], highlightTransition: [0.65, 0.8],
            referenceSampleCount: ref.count, sourceSampleCount: src.count,
            sixVectorEnabled: options.matchSixVectorColors !== false,
            matchScore: totalConfidence ? round(reliable.reduce((sum, band) => sum + band.matchScore * band.confidence, 0) / totalConfidence * (0.7 + 0.3 * coverageOverlap)) : null,
            bands, warnings: [...new Set(warnings)],
        };
    }
}
/** Conservative circular curves reuse measured vectors, not histogram population differences. */
export function createTonalCorrection(analysis) {
    if (!analysis.sixVectorEnabled)
        return undefined;
    // A grayscale / unsupported palette can have confident luminance bands but
    // no paired hue evidence. An empty tonal layer must not replace the existing
    // global hue correction just because the tone-analysis checkbox is enabled.
    if (!analysis.bands.some((band) => band.confidence >= 0.25 && band.colorVectors.vectors.some((vector) => vector.confidence >= 0.25 && vector.referenceHue !== null && vector.sourceHue !== null)))
        return undefined;
    return {
        workingColorSpace: analysis.workingColorSpace,
        ranges: analysis.bands.map((band) => ({
            range: band.range,
            confidence: band.confidence,
            hueVsHue: Array.from({ length: 24 }, (_, index) => {
                const hue = index * 15;
                let shift = 0, total = 0;
                for (const vector of band.colorVectors.vectors) {
                    if (vector.confidence < 0.25 || vector.sourceHue === null)
                        continue;
                    const distance = Math.abs(((hue - vector.sourceHue + 540) % 360) - 180);
                    const proximity = Math.max(0, 1 - distance / 48);
                    // Anchor at measured source hue, taper to zero outside evidenced colors.
                    shift += clamp(vector.hueShiftDegrees, -14, 14) * proximity * vector.confidence;
                    total += proximity;
                }
                return round(band.confidence >= 0.25 ? shift / Math.max(1, total) * band.confidence * 0.75 : 0);
            }),
        })),
    };
}
//# sourceMappingURL=TonalDistributionAnalyzer.js.map